import { useContext, useEffect, useRef } from 'react';
import EditorJS from '@editorjs/editorjs';
import Undo from 'editorjs-undo';
import { MessageContext } from '../../../../Context/MessageContext';

const useEditorJs = ({
    holderId,
    data,
    tools,
    placeholder,
    i18n,
    onChange,
}) => {
    const { pushMessage } = useContext(MessageContext);
    const editorRef = useRef(null);
    const undoRef = useRef(null);
    const editorElementRef = useRef(null);
    const mouseDownHandlerRef = useRef(null);
    const mouseUpHandlerRef = useRef(null);
    const clickHandlerRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const initialDataRef = useRef(data);
    const toolsRef = useRef(tools);
    const placeholderRef = useRef(placeholder);
    const clickContainerRef = useRef(null);
    const pushMessageRef = useRef(pushMessage);
    const i18nRef = useRef(i18n);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        initialDataRef.current = data;
    }, [data]);

    useEffect(() => {
        toolsRef.current = tools;
    }, [tools]);

    useEffect(() => {
        placeholderRef.current = placeholder;
    }, [placeholder]);

    useEffect(() => {
        pushMessageRef.current = pushMessage;
    }, [pushMessage]);

    useEffect(() => {
        i18nRef.current = i18n;
    }, [i18n]);

    useEffect(() => {
        let isMounted = true;

        const ensureCaretInWritablePlace = async (
            editor,
            clickContainer,
            editorElement,
            event,
        ) => {
            if (!clickContainer?.contains(event.target)) {
                return;
            }

            const clickedEditable = event.target.closest(
                '[contenteditable="true"], input, textarea, .ce-toolbar, .ce-inline-toolbar, .tc-popover',
            );

            if (clickedEditable) {
                return;
            }

            const clickedInsideBlock = event.target.closest('.ce-block');
            if (clickedInsideBlock) {
                return;
            }

            const getInsertIndexFromClickY = (container, clickY) => {
                const blockElements = Array.from(
                    container.querySelectorAll('.ce-block'),
                );

                if (!blockElements.length) {
                    return 0;
                }

                for (let index = 0; index < blockElements.length; index += 1) {
                    const rect = blockElements[index].getBoundingClientRect();
                    const middleY = rect.top + rect.height / 2;

                    if (clickY < middleY) {
                        return index;
                    }
                }

                return blockElements.length;
            };

            const isEmptyParagraphBlock = (blockApi) => {
                if (!blockApi || blockApi.name !== 'paragraph') {
                    return false;
                }

                const rawText =
                    blockApi.holder?.querySelector('[contenteditable="true"]')
                        ?.textContent || '';

                const normalized = rawText.replace(/\u200B/g, '').trim();
                return normalized.length === 0;
            };

            try {
                let count = editor.blocks.getBlocksCount();

                if (count === 0) {
                    await editor.blocks.insert(
                        'paragraph',
                        { text: '' },
                        undefined,
                        0,
                        true,
                    );
                    editor.caret.setToBlock(0, 'end');
                    return;
                }

                const insertIndex = getInsertIndexFromClickY(
                    editorElement || clickContainer,
                    event.clientY,
                );

                if (insertIndex < count) {
                    const targetBlock =
                        editor.blocks.getBlockByIndex(insertIndex);
                    if (isEmptyParagraphBlock(targetBlock)) {
                        editor.caret.setToBlock(insertIndex, 'end');
                        return;
                    }
                }

                if (insertIndex > 0) {
                    const previousBlock = editor.blocks.getBlockByIndex(
                        insertIndex - 1,
                    );
                    if (isEmptyParagraphBlock(previousBlock)) {
                        editor.caret.setToBlock(insertIndex - 1, 'end');
                        return;
                    }
                }

                await editor.blocks.insert(
                    'paragraph',
                    { text: '' },
                    undefined,
                    insertIndex,
                    true,
                );

                count = editor.blocks.getBlocksCount();
                const safeIndex = Math.min(insertIndex, count - 1);
                editor.caret.setToBlock(safeIndex, 'end');
            } catch (error) {
                pushMessageRef.current?.(
                    'EditorJS caret positioning failed',
                    error,
                );
            }
        };

        const initEditor = async () => {
            const editor = new EditorJS({
                holder: holderId,
                autofocus: true,
                data: initialDataRef.current,
                placeholder: placeholderRef.current,
                tools: toolsRef.current,
                minHeight: 0,
                i18n: i18nRef.current,
                async onChange(api) {
                    try {
                        const savedData = await api.saver.save();
                        onChangeRef.current(savedData);
                    } catch (error) {
                        pushMessageRef.current?.('EditorJS save failed', error);
                    }
                },
                onReady() {
                    if (!isMounted) {
                        return;
                    }

                    editorRef.current = editor;

                    const undo = new Undo({
                        editor,
                        config: {
                            shortcuts: {
                                undo: 'CMD+Z',
                                redo: 'CMD+SHIFT+Z',
                            },
                        },
                    });

                    undo.initialize(
                        initialDataRef.current || {
                            time: Date.now(),
                            blocks: [],
                        },
                    );

                    undoRef.current = undo;

                    const editorElement = document.getElementById(holderId);
                    editorElementRef.current = editorElement;

                    const clickContainer =
                        editorElement?.parentElement || editorElement;
                    clickContainerRef.current = clickContainer;

                    const handleMouseDown = (event) => {
                        const toolbar =
                            document.querySelector('.ce-inline-toolbar');

                        if (!toolbar) {
                            return;
                        }

                        if (toolbar.contains(event.target)) {
                            return;
                        }

                        toolbar.style.visibility = 'hidden';
                    };

                    const handleMouseUp = () => {
                        const toolbar =
                            document.querySelector('.ce-inline-toolbar');

                        if (toolbar) {
                            toolbar.style.visibility = '';
                        }
                    };

                    const handleClick = async (event) => {
                        await ensureCaretInWritablePlace(
                            editor,
                            clickContainer,
                            editorElement,
                            event,
                        );
                    };

                    mouseDownHandlerRef.current = handleMouseDown;
                    mouseUpHandlerRef.current = handleMouseUp;
                    clickHandlerRef.current = handleClick;

                    editorElement?.addEventListener(
                        'mousedown',
                        handleMouseDown,
                    );
                    editorElement?.addEventListener('mouseup', handleMouseUp);
                    clickContainer?.addEventListener('click', handleClick);
                },
            });

            try {
                await editor.isReady;
            } catch (error) {
                pushMessageRef.current?.(
                    'EditorJS initialization failed',
                    error,
                );
            }
        };

        initEditor();

        return () => {
            isMounted = false;

            const editorElement = editorElementRef.current;
            const clickContainer = clickContainerRef.current;
            const mouseDownHandler = mouseDownHandlerRef.current;
            const mouseUpHandler = mouseUpHandlerRef.current;
            const clickHandler = clickHandlerRef.current;

            if (editorElement && mouseDownHandler) {
                editorElement.removeEventListener(
                    'mousedown',
                    mouseDownHandler,
                );
            }

            if (editorElement && mouseUpHandler) {
                editorElement.removeEventListener('mouseup', mouseUpHandler);
            }

            if (clickContainer && clickHandler) {
                clickContainer.removeEventListener('click', clickHandler);
            }

            mouseDownHandlerRef.current = null;
            mouseUpHandlerRef.current = null;
            clickHandlerRef.current = null;
            clickContainerRef.current = null;
            editorElementRef.current = null;
            undoRef.current = null;

            if (editorRef.current?.destroy) {
                editorRef.current.destroy();
                editorRef.current = null;
            }
        };
    }, [holderId]);

    return editorRef;
};

export default useEditorJs;
